/**
 * MealPreviewCard - Lightweight meal preview component
 *
 * This component displays a simple preview of meals without drag-drop functionality.
 * Used for displaying meals in overlap resolution and other preview contexts.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useAssets } from '../contexts';
import { getPrimaryMeal } from '@utils/Meals';
import ImgResized from '@components/Widgets/ImgResized.react';

import './MealPreviewCard.scss';

const MealPreviewCard = ({ meals, className }) => {
    const { recipes, foods } = useAssets();

    if (meals.length === 0) {
        return (
            <div className={classnames('meal-preview-card', 'meal-preview-card--empty', className)}>
                <span className="meal-preview-card__empty-text">No meals</span>
            </div>
        );
    }

    // Use getPrimaryMeal to get all the meal data
    const mealData = getPrimaryMeal(meals, recipes, foods);
    const { titles, photos } = mealData;

    // Combine titles for display
    const displayTitle = titles.length > 0 ? titles.join(' + ') : 'Meals';
    const primaryPhoto = photos[0]?.url; // Use first available photo URL
    const hasLeftover = meals.some(meal => meal.meal_type === 'leftover');

    return (
        <div className={classnames('meal-preview-card', className)}>
            <div className="item">
                <div className="image-container">
                    {primaryPhoto && (
                        <ImgResized
                            className="meal-image"
                            src={primaryPhoto}
                            alt={displayTitle}
                            width={180}
                            height={127}
                        />
                    )}
                    <div className="meal-info">
                        <p>
                            {hasLeftover && <em>Leftover: </em>}
                            {displayTitle}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

MealPreviewCard.propTypes = {
    meals: PropTypes.arrayOf(PropTypes.shape({
        uuid: PropTypes.string,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        recipe_uuid: PropTypes.string,
        food_uuid: PropTypes.string,
        meal_type: PropTypes.string
    })).isRequired,
    className: PropTypes.string
};

export default MealPreviewCard;
